import _typeof from "@babel/runtime/helpers/typeof";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _get from "lodash/get";
import _includes from "lodash/includes";
var _excluded = ["CtaComponent"];
import "core-js/modules/es6.string.link.js";
import "core-js/modules/es6.regexp.replace.js";
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { v4 as uuidV4 } from 'uuid';
import analytics from '@nerdwallet/analytics';
import { getProductType, getInstitutionId, getProductId, getOfferId } from '@nerdwallet/marketplace/selectors/common';
import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import ApplyNowWrapper from './apply-now-wrapper';
import { LINK_TYPES } from './constants';
import mapMarketplaceEntityToProps, { linksToReview } from './mappings';
import { getFullUrl } from './utils/url';
import { isThrottled } from './utils/throttling';
var PRODUCT_TYPES_REQUIRING_DISPLAY_PAGE = [PRODUCT_TYPES.BANKING];
var BaseMarketplaceApplyNow = function BaseMarketplaceApplyNow(_ref) {
  var CtaComponent = _ref.CtaComponent,
    props = _objectWithoutProperties(_ref, _excluded);
  return CtaComponent ? /*#__PURE__*/React.createElement(CtaComponent, props) : null;
};
BaseMarketplaceApplyNow.propTypes = {
  /* The component to render. Defaults to ApplyNowWrapper. */
  CtaComponent: PropTypes.any // eslint-disable-line react/forbid-prop-types
};

BaseMarketplaceApplyNow.defaultProps = {
  CtaComponent: ApplyNowWrapper
};
var trackNonMonetizingShoppingFinished = function trackNonMonetizingShoppingFinished(_ref2) {
  var _window, _window$location;
  var analyticsMetadata = _ref2.analyticsMetadata,
    botSuspected = _ref2.botSuspected,
    href = _ref2.href,
    impressionId = _ref2.impressionId,
    link = _ref2.link,
    marketplaceEntity = _ref2.marketplaceEntity;
  // Avoid executing this function in SSR
  if ((typeof window === "undefined" ? "undefined" : _typeof(window)) !== 'object') {
    return;
  }
  var elementPosition = analyticsMetadata.elementPosition,
    pageNumber = analyticsMetadata.pageNumber,
    sectionName = analyticsMetadata.sectionName,
    sectionPosition = analyticsMetadata.sectionPosition;
  var payload = {
    // We need to strip the hyphens since shopping_finished IDs are special (defined as ^[A-Za-z0-9]{32}$ and not as a guid)
    shopping_finish_id: uuidV4().replace(/-/g, ''),
    finish_type: 'internal_content',
    source_url_simple: (_window = window) === null || _window === void 0 ? void 0 : (_window$location = _window.location) === null || _window$location === void 0 ? void 0 : _window$location.href,
    destination_url: getFullUrl(href || link),
    cta_type: 'apply_now_button',
    bot_suspected: botSuspected,
    marketplace_product_type: getProductType(marketplaceEntity),
    marketplace_institution_id: getInstitutionId(marketplaceEntity),
    marketplace_product_id: getProductId(marketplaceEntity),
    marketplace_offer_id: getOfferId(marketplaceEntity),
    // NOTE: this is a per-vertical opt-in feature. By default, isThrottled() will return false for all verticals.
    // Since we are rendering a review link, we assume that this is not monetizing.
    monetizable: isThrottled(marketplaceEntity) ? 'no_because_of_limit' : 'no',
    driver: 'user_selected',
    product_impression_id: impressionId,
    page_number: pageNumber,
    section_name: sectionName,
    section_position: sectionPosition,
    element_position: elementPosition
  };
  analytics.track('shopping_finished', payload);
};
var MarketplaceApplyNow = function MarketplaceApplyNow(props) {
  var mappedProps = mapMarketplaceEntityToProps(props);
  if (mappedProps.impressionId) mappedProps.productImpressionId = mappedProps.impressionId;
  var children = mappedProps.children;

  // Track non-monetizing shopping_finished event when rendering a review link that could have otherwise monetized.
  if (linksToReview(mappedProps)) {
    mappedProps.onClick = function () {
      return trackNonMonetizingShoppingFinished(mappedProps);
    };
  }
  return mappedProps.link ? /*#__PURE__*/React.createElement(BaseMarketplaceApplyNow, mappedProps) : /*#__PURE__*/React.createElement(Fragment, null, children);
};

// Note that all props are passed directly to the CtaComponent that is rendered. Check the
// CtaComponent's file for props specific to that component. Most productTypes will use
// ApplyNowWrapper. Non-monetizing entities may use review-link.
MarketplaceApplyNow.propTypes = {
  /** Metadata for use in related analytics events */
  analyticsMetadata: PropTypes.shape({
    pageNumber: PropTypes.number,
    elementPosition: PropTypes.number,
    sectionName: PropTypes.string,
    sectionPosition: PropTypes.number
  }),
  /** Is the user suspected to be a bot? Defaults to `false`.
   *  This is required for firing a `shopping_finished` event and can be filled if the consumer apps is
   *  able to detect this (using CloudFlare headers or otherwise).
   */
  botSuspected: PropTypes.bool,
  /** Disable annotation text */
  disableAnnotation: PropTypes.bool,
  /** Hide institution name from annotation text (only banking for now) */
  hideInstitutionFromAnnotation: PropTypes.bool,
  /** The displayPage name for the page this component will be rendered on. This is required
      for `banking`. Must be one of:
      https://github.com/NerdWallet/react-components/blob/master/components/banking-product-cta/src/banking-product-cta.jsx#L13 */
  displayPage: function displayPage(props, propName, componentName) {
    var productType = getProductType(props.marketplaceEntity);
    if (_includes(PRODUCT_TYPES_REQUIRING_DISPLAY_PAGE, productType) && typeof props[propName] !== 'string') {
      return new Error("Prop ".concat(propName, " is required when \"productType\" is set to \"banking\" for ").concat(componentName, "."));
    }
    return null;
  },
  /** Href for the review link (used if the offer is non-monetizing) */
  reviewLink: PropTypes.string,
  /** Whether or not an impression event should be logged.
   Defaults to true and should only be set to false for testing/storybook purposes. */
  shouldLogImpression: PropTypes.bool,
  /** Marketplace entity to render. */
  marketplaceEntity: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types
  /** The type of component. If not provided, this will try to be derived from `type`. */
  trkElement: PropTypes.string,
  /** The page type to use for tracking */
  trkPageType: PropTypes.string,
  /** Where on the page the component is */
  trkSectionCategory: PropTypes.string,
  /** The page taxonomy to use for tracking. Defaults to the nwPageTaxonomy stored on the window. */
  trkTaxonomy: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  /** The link type (button vs. link). Must be one of:
      https://github.com/NerdWallet/react-components/blob/master/components/marketplace-apply-now/src/constants.js#L2
      Defaults to `BUTTON`. */
  type: PropTypes.oneOf([LINK_TYPES.BUTTON, LINK_TYPES.LINK])
};
MarketplaceApplyNow.defaultProps = {
  analyticsMetadata: {
    pageNumber: null,
    elementPosition: null,
    sectionName: null,
    sectionPosition: null
  },
  botSuspected: false,
  disableAnnotation: false,
  hideInstitutionFromAnnotation: false,
  displayPage: null,
  reviewLink: null,
  shouldLogImpression: true,
  trkElement: null,
  trkPageType: null,
  trkSectionCategory: null,
  trkTaxonomy: _get(global, 'window.nwPageTaxonomy'),
  type: LINK_TYPES.BUTTON
};
export default MarketplaceApplyNow;